import React, { useState, useEffect } from 'react';
import { Cookie, X, Check, ExternalLink } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useAnalytics } from '../context/GoogleAnalyticsContext';
import { safeLocalStorage } from '../utils/safeStorage';

const COOKIE_CONSENT_KEY = 'carp_arena_cookie_consent';

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const { setConsent } = useAnalytics();

  useEffect(() => {
    try {
      const consent = safeLocalStorage.getItem(COOKIE_CONSENT_KEY);
      if (!consent) {
        // Небольшая задержка для плавного появления после начальной загрузки страницы
        const timer = setTimeout(() => {
          setIsVisible(true);
        }, 800);
        return () => clearTimeout(timer);
      }
    } catch {
      // Игнорируем ошибки
    }
  }, []);

  const handleAccept = () => {
    setConsent(true);
    setIsVisible(false);
  };

  const handleDecline = () => {
    setConsent(false);
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.95 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-50 pointer-events-auto"
          role="region"
          aria-label="Согласие на использование cookie"
        >
          <div className="bg-white/95 backdrop-blur-md rounded-2xl p-5 shadow-2xl border border-slate-200/90 text-slate-800 flex flex-col gap-3.5 relative ring-1 ring-black/5">
            <button
              type="button"
              onClick={handleDecline}
              className="absolute top-3.5 right-3.5 p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
              title="Закрыть"
              aria-label="Закрыть уведомление"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3.5 pr-6">
              <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 shrink-0">
                <Cookie className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-slate-900 leading-tight">
                  Мы используем файлы cookie
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Они обеспечивают корректную работу авторизации, сохранение настроек и сбор базовой статистики для улучшения сервиса.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 pt-1 border-t border-slate-100">
              <a
                href="/faq"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-slate-500 hover:text-emerald-800 font-medium inline-flex items-center gap-1 transition-colors self-center sm:self-auto"
              >
                Подробнее в FAQ
                <ExternalLink className="w-3 h-3" />
              </a>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleAccept}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-semibold rounded-xl shadow-sm hover:shadow transition-all active:scale-[0.98]"
                >
                  <Check className="w-3.5 h-3.5" />
                  Принять все
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

